<template>
  <div class="patient-snapshot" v-if="hasContent">
    <template>
      <div class="patient-snapshot-group" :class="allergyLevel">
        <span class="patient-snapshot-label">
          <fa-icon :icon="['fal', 'exclamation-triangle']" /> Alergias
        </span>
        <div class="patient-snapshot-items">
          <span
            class="patient-snapshot-chip allergy"
            v-for="(item, i) in allergySubstances"
            :key="i"
          >
            {{ item.name }}
            <small v-if="severityLabel(item.severity)">({{ severityLabel(item.severity) }})</small>
          </span>
          <span class="patient-snapshot-empty" v-if="allergySubstances.length === 0">
            {{ allergyLabel }}
          </span>
        </div>
      </div>

      <div class="patient-snapshot-group" v-if="conditions.length > 0">
        <span class="patient-snapshot-label">
          <fa-icon :icon="['fal', 'heartbeat']" /> Condições registradas
        </span>
        <div class="patient-snapshot-items">
          <span
            class="patient-snapshot-chip tooltip"
            v-for="(item, i) in visible('conditions', conditions)"
            :key="i"
            :data-tooltip="conditionTooltip(item)"
          >{{ item.code }} - {{ shorten(item.name) }}</span>
          <button
            type="button"
            class="patient-snapshot-more"
            v-if="hidden('conditions', conditions) > 0"
            @click="toggle('conditions')"
          >+{{ hidden('conditions', conditions) }}</button>
          <button
            type="button"
            class="patient-snapshot-more"
            v-else-if="expanded.conditions"
            @click="toggle('conditions')"
          >menos</button>
        </div>
      </div>

      <div class="patient-snapshot-group" v-if="medications.length > 0">
        <span class="patient-snapshot-label">
          <fa-icon :icon="['fal', 'capsules']" /> Medicações recentes
        </span>
        <div class="patient-snapshot-items">
          <span
            class="patient-snapshot-chip tooltip"
            v-for="(item, i) in visible('medications', medications)"
            :key="i"
            :data-tooltip="medicationTooltip(item)"
          >{{ shorten(item.name) }}</span>
          <button
            type="button"
            class="patient-snapshot-more"
            v-if="hidden('medications', medications) > 0"
            @click="toggle('medications')"
          >+{{ hidden('medications', medications) }}</button>
          <button
            type="button"
            class="patient-snapshot-more"
            v-else-if="expanded.medications"
            @click="toggle('medications')"
          >menos</button>
        </div>
      </div>

      <div class="patient-snapshot-group" v-if="vitals.length > 0">
        <span class="patient-snapshot-label">
          <fa-icon :icon="['fal', 'monitor-heart-rate']" /> Últimas medidas
        </span>
        <div class="patient-snapshot-items">
          <span
            class="patient-snapshot-chip tooltip"
            v-for="(item, i) in vitals"
            :key="i"
            :data-tooltip="`Registrado em ${formatDate(item.date)}`"
          >{{ item.label }}: <strong>{{ item.value }}</strong> {{ item.unit }}</span>
          <button
            type="button"
            class="patient-snapshot-chart tooltip"
            data-tooltip="Ver evolução"
            @click="chartModal = true"
          ><fa-icon :icon="['fal', 'chart-line']" /> Evolução</button>
        </div>
      </div>
    </template>

    <vitals-chart
      v-if="chartModal"
      :show="chartModal"
      :patient-id="patientId"
      @close="chartModal = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import VitalsChart from './VitalsChart.vue';

const SEVERITIES = {
  low: 'leve',
  medium: 'moderada',
  high: 'grave',
};

const ALLERGY_LABELS = {
  deny: 'Nega alergias',
  unknown: 'Não informado',
  allergic: 'Sem substâncias detalhadas',
};

const VITAL_LABELS = [
  ['bloodPressure', 'PA', 'mmHg'],
  ['weight', 'Peso', 'kg'],
  ['height', 'Altura', 'cm'],
  ['heartRate', 'FC', 'bpm'],
  ['respiratoryFrequency', 'FR', 'mpm'],
  ['temperature', 'Temp', 'ºC'],
  ['saturation', 'SatO²', '%'],
  ['abdominalCircumference', 'C. abd.', 'cm'],
];

const MAX_NAME_LENGTH = 32;
const COLLAPSE_LIMIT = 3;

export default {
  components: {
    VitalsChart,
  },
  props: {
    patientId: {
      type: String,
      default: '',
    },
    snapshot: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      chartModal: false,
      expanded: {
        conditions: false,
        medications: false,
      },
    };
  },
  watch: {
    snapshot() {
      this.expanded.conditions = false;
      this.expanded.medications = false;
    },
  },
  computed: {
    allergy() {
      return (this.snapshot && this.snapshot.allergy) || { type: 'unknown', substances: [] };
    },
    allergySubstances() {
      return this.allergy.substances || [];
    },
    allergyLabel() {
      return ALLERGY_LABELS[this.allergy.type] || ALLERGY_LABELS.unknown;
    },
    allergyLevel() {
      if (this.allergySubstances.length > 0) {
        return 'danger';
      }
      return this.allergy.type === 'deny' ? '' : 'warning';
    },
    conditions() {
      return (this.snapshot && this.snapshot.conditions) || [];
    },
    medications() {
      return (this.snapshot && this.snapshot.medications) || [];
    },
    vitals() {
      const values = (this.snapshot && this.snapshot.vitals) || {};

      return VITAL_LABELS
        .filter(([field]) => values[field])
        .map(([field, label, unit]) => ({
          label,
          unit,
          value: values[field].value,
          date: values[field].date,
        }));
    },
    hasContent() {
      return !!this.snapshot;
    },
  },
  methods: {
    visible(key, items) {
      return this.expanded[key] ? items : items.slice(0, COLLAPSE_LIMIT);
    },
    hidden(key, items) {
      return this.expanded[key] ? 0 : Math.max(items.length - COLLAPSE_LIMIT, 0);
    },
    toggle(key) {
      this.expanded[key] = !this.expanded[key];
    },
    severityLabel(severity) {
      return SEVERITIES[severity] || '';
    },
    shorten(name) {
      const value = name || '';
      return value.length > MAX_NAME_LENGTH
        ? `${value.substring(0, MAX_NAME_LENGTH)}...`
        : value;
    },
    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '';
    },
    conditionTooltip({ name, count, lastDate }) {
      const times = count > 1 ? `${count} atendimentos` : '1 atendimento';
      return `${name} — ${times}, último em ${this.formatDate(lastDate)}`;
    },
    medicationTooltip({ name, dosage, lastDate }) {
      const detail = dosage ? `${name} (${dosage})` : name;
      return `${detail} — prescrito em ${this.formatDate(lastDate)}`;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.patient-snapshot {
  align-items: flex-start;
  background-color: $gray-color-ultra-light;
  border-bottom: $border-width solid $border-color;
  display: flex;
  flex-wrap: wrap;
  gap: $layout-spacing $layout-spacing-lg;
  padding: $layout-spacing $layout-spacing-lg;

  .patient-snapshot-group {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    max-width: 100%;

    &.warning .patient-snapshot-label {
      color: $orange-color-700;
    }

    &.danger .patient-snapshot-label {
      color: $red-color-700;
    }
  }

  .patient-snapshot-label {
    color: $gray-color-700;
    font-size: $font-size-sm;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;

    svg {
      margin-right: $layout-spacing-sm;
    }
  }

  .patient-snapshot-items {
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
  }

  .patient-snapshot-chip {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    color: $gray-color-900;
    font-size: $font-size-sm;
    padding: 0 $layout-spacing;
    white-space: nowrap;

    &.allergy {
      background-color: $red-color-100;
      border-color: $red-color-300;
      color: $red-color-700;
      font-weight: 600;
    }
  }

  .patient-snapshot-more {
    background: none;
    border: none;
    color: $info-color;
    cursor: pointer;
    font-size: $font-size-sm;
    font-weight: 600;
    padding: 0 $layout-spacing-sm;
  }

  .patient-snapshot-chart {
    background: none;
    border: $border-width dashed $border-color-dark;
    border-radius: $border-radius;
    color: $info-color;
    cursor: pointer;
    font-size: $font-size-sm;
    padding: 0 $layout-spacing;
    white-space: nowrap;

    svg {
      margin-right: $layout-spacing-sm;
    }

    &:hover {
      border-color: $info-color;
    }
  }

  .patient-snapshot-empty {
    color: $gray-color-700;
    font-size: $font-size-sm;
  }
}
</style>
